'use client';

import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleX,
} from '@tabler/icons-react';
import { FileDocumentAnalysis } from 'bff';
import { useTranslations } from 'i11n';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui';

const INFERENCE_LABEL_KEYS: Record<string, string> = {
  ia_detectada_images: 'inferences.ia_detectada_images',
  similitud_web_images: 'inferences.similitud_web_images',
  pdf_forgery_verdict: 'inferences.pdf_forgery_verdict',
  pdf_forgery_trust_score: 'inferences.pdf_forgery_trust_score',
};

/**
 * Discrete per-file verdict of the Autoinspector document validation. Only
 * rendered where the API sent `document_analysis` (insurer views): the
 * claimant side never receives the field, so the badge cannot leak there.
 */
export const DocumentAnalysisBadge: React.FC<{
  analysis: FileDocumentAnalysis;
  className?: string;
}> = ({ analysis, className }) => {
  const t = useTranslations('documentAnalysis');

  const reasons = analysis.inferences.map((identifier) => {
    const key = INFERENCE_LABEL_KEYS[identifier];
    return key ? t(key) : t('inferences.unknown');
  });

  const badge =
    analysis.verdict === 'disapproved'
      ? {
          icon: IconCircleX,
          color: 'text-red-600',
          title: t('disapproved'),
        }
      : analysis.verdict === 'needs_review'
      ? {
          icon: IconAlertTriangle,
          color: 'text-amber-500',
          title: t('needsReview'),
        }
      : analysis.verdict === 'approved'
      ? {
          icon: IconCircleCheck,
          color: 'text-green-600',
          title: t('approved'),
        }
      : {
          icon: IconCircleDashed,
          color: 'text-stone-400',
          title: t('noResult'),
        };

  const Icon = badge.icon;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full bg-white/90 p-0.5 shadow-sm',
              className,
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <Icon className={cn('h-4 w-4', badge.color)} />
          </span>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-xs'>
          <p className='text-xs font-medium'>{badge.title}</p>
          {reasons.length > 0 && (
            <ul className='mt-1 list-disc pl-4 text-xs'>
              {reasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
